import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type Pagamento = Database['public']['Tables']['pagamentos']['Row'];
type Contrato = Database['public']['Tables']['contratos']['Row'];
type Comissao = Database['public']['Tables']['comissoes']['Row'];

export interface DateRange {
  inicio: string;
  fim: string;
}

export interface FinancialSummary {
  receitas: {
    total: number;
    contratos: number;
    comissoes_pagas: number;
  };
  despesas: {
    total: number;
    comissoes_pendentes: number;
  };
  saldo: number;
  contratos_ativos: number;
  contratos_vencidos: number;
}

export interface CashFlow {
  mes: string;
  receitas: number;
  despesas: number;
  saldo: number;
  contratos_novos: number;
}

export interface Revenue {
  id: string;
  tipo: 'contrato' | 'comissao';
  descricao: string;
  valor: number;
  data: string;
  status: string;
}

export interface Expense {
  id: string;
  tipo: 'comissao';
  descricao: string;
  valor: number;
  data: string;
  status: string;
}

export interface PaymentSummary {
  total_pendente: number;
  total_pago: number;
  total_cancelado: number;
  proximos_vencimentos: Pagamento[];
}

export class FinanceiroSupabaseService {
  /**
   * Obtém resumo financeiro do período
   */
  async getResumoFinanceiro(tenantId: string, periodo: DateRange): Promise<FinancialSummary> {
    try {
      // Buscar receitas (contratos ativos)
      const { data: contratos, error: contratosError } = await supabase
        .from('contratos')
        .select('id, valor_credito, valor_entrada, status, created_at')
        .eq('tenant_id', tenantId)
        .eq('status', 'ATIVO')
        .gte('created_at', periodo.inicio)
        .lte('created_at', periodo.fim)
        .is('deleted_at', null);

      if (contratosError) throw contratosError;

      // Buscar comissões pagas
      const { data: comissoesPagas, error: comissoesError } = await supabase
        .from('comissoes')
        .select('id, valor, status_pagamento')
        .eq('tenant_id', tenantId)
        .eq('status_pagamento', 'PAGA')
        .gte('data_pagamento', periodo.inicio)
        .lte('data_pagamento', periodo.fim)
        .is('deleted_at', null);

      if (comissoesError) throw comissoesError;

      // Buscar comissões pendentes
      const { data: comissoesPendentes, error: pendentesError } = await supabase
        .from('comissoes')
        .select('id, valor')
        .eq('tenant_id', tenantId)
        .eq('status_pagamento', 'PENDENTE')
        .is('deleted_at', null);

      if (pendentesError) throw pendentesError;

      // Calcular totais
      const totalReceitas = contratos?.reduce((sum, c) => sum + (c.valor_credito || 0), 0) || 0;
      const totalComissoesPagas = comissoesPagas?.reduce((sum, c) => sum + (c.valor || 0), 0) || 0;
      const totalComissoesPendentes = comissoesPendentes?.reduce((sum, c) => sum + (c.valor || 0), 0) || 0;

      return {
        receitas: {
          total: totalReceitas,
          contratos: contratos?.length || 0,
          comissoes_pagas: totalComissoesPagas
        },
        despesas: {
          total: totalComissoesPendentes,
          comissoes_pendentes: comissoesPendentes?.length || 0
        },
        saldo: totalReceitas - totalComissoesPendentes,
        contratos_ativos: contratos?.length || 0,
        contratos_vencidos: 0 // Implementar lógica de vencimento se necessário
      };
    } catch (error) {
      console.error('Erro ao obter resumo financeiro:', error);
      throw error;
    }
  }

  /**
   * Obtém fluxo de caixa dos últimos meses
   */
  async getFluxoCaixa(tenantId: string, meses: number = 12): Promise<CashFlow[]> {
    try {
      const fluxoCaixa: CashFlow[] = [];
      const dataAtual = new Date();

      for (let i = meses - 1; i >= 0; i--) {
        const dataInicio = new Date(dataAtual.getFullYear(), dataAtual.getMonth() - i, 1);
        const dataFim = new Date(dataAtual.getFullYear(), dataAtual.getMonth() - i + 1, 0);
        
        const mes = `${dataInicio.getFullYear()}-${(dataInicio.getMonth() + 1).toString().padStart(2, '0')}`;

        // Buscar receitas do mês
        const { data: receitas, error: receitasError } = await supabase
          .from('contratos')
          .select('valor_credito, valor_entrada')
          .eq('tenant_id', tenantId)
          .eq('status', 'ATIVO')
          .gte('created_at', dataInicio.toISOString())
          .lte('created_at', dataFim.toISOString())
          .is('deleted_at', null);

        if (receitasError) throw receitasError;

        // Buscar despesas do mês (comissões pagas)
        const { data: despesas, error: despesasError } = await supabase
          .from('comissoes')
          .select('valor')
          .eq('tenant_id', tenantId)
          .eq('status_pagamento', 'PAGA')
          .gte('data_pagamento', dataInicio.toISOString())
          .lte('data_pagamento', dataFim.toISOString())
          .is('deleted_at', null);

        if (despesasError) throw despesasError;

        const totalReceitas = receitas?.reduce((sum, c) => sum + (c.valor_credito || 0), 0) || 0;
        const totalDespesas = despesas?.reduce((sum, c) => sum + (c.valor || 0), 0) || 0;

        fluxoCaixa.push({
          mes,
          receitas: totalReceitas,
          despesas: totalDespesas,
          saldo: totalReceitas - totalDespesas,
          contratos_novos: receitas?.length || 0
        });
      }

      return fluxoCaixa;
    } catch (error) {
      console.error('Erro ao obter fluxo de caixa:', error);
      throw error;
    }
  }

  /**
   * Obtém pagamentos pendentes
   */
  async getPagamentosPendentes(tenantId: string): Promise<Pagamento[]> {
    try {
      const { data, error } = await supabase
        .from('pagamentos')
        .select(`
          *,
          contratos!inner(
            *,
            clientes!inner(*)
          )
        `)
        .eq('tenant_id', tenantId)
        .eq('status', 'PENDENTE')
        .order('data_vencimento', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao obter pagamentos pendentes:', error);
      throw error;
    }
  }

  /**
   * Obtém resumo de pagamentos
   */
  async getPaymentSummary(tenantId: string): Promise<PaymentSummary> {
    try {
      // Buscar todos os pagamentos
      const { data: pagamentos, error } = await supabase
        .from('pagamentos')
        .select('*')
        .eq('tenant_id', tenantId)
        .is('deleted_at', null);

      if (error) throw error;

      // Calcular totais por status
      const totalPendente = pagamentos?.filter(p => p.status === 'PENDENTE')
        .reduce((sum, p) => sum + (p.valor || 0), 0) || 0;
      
      const totalPago = pagamentos?.filter(p => p.status === 'PAGA')
        .reduce((sum, p) => sum + (p.valor || 0), 0) || 0;
      
      const totalCancelado = pagamentos?.filter(p => p.status === 'CANCELADA')
        .reduce((sum, p) => sum + (p.valor || 0), 0) || 0;

      // Buscar próximos vencimentos (próximos 30 dias)
      const dataLimite = new Date();
      dataLimite.setDate(dataLimite.getDate() + 30);

      const { data: proximosVencimentos, error: vencimentosError } = await supabase
        .from('pagamentos')
        .select(`
          *,
          contratos!inner(
            *,
            clientes!inner(*)
          )
        `)
        .eq('tenant_id', tenantId)
        .eq('status', 'PENDENTE')
        .lte('data_vencimento', dataLimite.toISOString().split('T')[0])
        .order('data_vencimento', { ascending: true })
        .limit(10);

      if (vencimentosError) throw vencimentosError;

      return {
        total_pendente: totalPendente,
        total_pago: totalPago,
        total_cancelado: totalCancelado,
        proximos_vencimentos: proximosVencimentos || []
      };
    } catch (error) {
      console.error('Erro ao obter resumo de pagamentos:', error);
      throw error;
    }
  }

  /**
   * Obtém receitas do período
   */
  async getReceitas(tenantId: string, periodo: DateRange): Promise<Revenue[]> {
    try {
      // Buscar contratos ativos
      const { data: contratos, error: contratosError } = await supabase
        .from('contratos')
        .select(`
          *,
          clientes!inner(*),
          consultores!inner(*)
        `)
        .eq('tenant_id', tenantId)
        .eq('status', 'ATIVO')
        .gte('created_at', periodo.inicio)
        .lte('created_at', periodo.fim)
        .is('deleted_at', null);

      if (contratosError) throw contratosError;

      // Buscar comissões pagas
      const { data: comissoes, error: comissoesError } = await supabase
        .from('comissoes')
        .select(`
          *,
          contratos!inner(
            *,
            clientes!inner(*)
          ),
          consultores!inner(*)
        `)
        .eq('tenant_id', tenantId)
        .eq('status_pagamento', 'PAGA')
        .gte('data_pagamento', periodo.inicio)
        .lte('data_pagamento', periodo.fim)
        .is('deleted_at', null);

      if (comissoesError) throw comissoesError;

      const receitas: Revenue[] = [];

      // Adicionar contratos como receitas
      contratos?.forEach(contrato => {
        receitas.push({
          id: contrato.id,
          tipo: 'contrato',
          descricao: `Contrato ${contrato.codigo} - ${contrato.clientes?.nome}`,
          valor: contrato.valor_credito || 0,
          data: contrato.created_at,
          status: contrato.status || 'ATIVO'
        });
      });

      // Adicionar comissões pagas como receitas
      comissoes?.forEach(comissao => {
        receitas.push({
          id: comissao.id,
          tipo: 'comissao',
          descricao: `Comissão ${comissao.tipo} - ${comissao.consultores?.nome}`,
          valor: comissao.valor || 0,
          data: comissao.data_pagamento || comissao.created_at,
          status: comissao.status_pagamento || 'PAGA'
        });
      });

      return receitas.sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime());
    } catch (error) {
      console.error('Erro ao obter receitas:', error);
      throw error;
    }
  }

  /**
   * Obtém despesas do período
   */
  async getDespesas(tenantId: string, periodo: DateRange): Promise<Expense[]> {
    try {
      const { data: comissoes, error } = await supabase
        .from('comissoes')
        .select(`
          *,
          contratos!inner(
            *,
            clientes!inner(*)
          ),
          consultores!inner(*)
        `)
        .eq('tenant_id', tenantId)
        .eq('status_pagamento', 'PAGA')
        .gte('data_pagamento', periodo.inicio)
        .lte('data_pagamento', periodo.fim)
        .is('deleted_at', null);

      if (error) throw error;

      const despesas: Expense[] = comissoes?.map(comissao => ({
        id: comissao.id,
        tipo: 'comissao',
        descricao: `Comissão ${comissao.tipo} - ${comissao.consultores?.nome} - Contrato ${comissao.contratos?.codigo}`,
        valor: comissao.valor || 0,
        data: comissao.data_pagamento || comissao.created_at,
        status: comissao.status_pagamento || 'PAGA'
      })) || [];

      return despesas.sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime());
    } catch (error) {
      console.error('Erro ao obter despesas:', error);
      throw error;
    }
  }

  /**
   * Obtém estatísticas financeiras por consultor
   */
  async getEstatisticasPorConsultor(tenantId: string, periodo?: DateRange): Promise<any[]> {
    try {
      let query = supabase
        .from('comissoes')
        .select(`
          *,
          consultores!inner(*),
          contratos!inner(*)
        `)
        .eq('tenant_id', tenantId)
        .is('deleted_at', null);

      if (periodo) {
        query = query.gte('created_at', periodo.inicio).lte('created_at', periodo.fim);
      }

      const { data: comissoes, error } = await query;

      if (error) throw error;

      // Agrupar por consultor
      const estatisticas: { [key: string]: any } = {};

      comissoes?.forEach(comissao => {
        const consultorId = comissao.consultor_id;
        
        if (!estatisticas[consultorId]) {
          estatisticas[consultorId] = {
            consultor: comissao.consultores,
            total_comissoes: 0,
            comissoes_pagas: 0,
            comissoes_pendentes: 0,
            valor_total: 0,
            valor_pago: 0,
            valor_pendente: 0
          };
        }

        estatisticas[consultorId].total_comissoes++;
        estatisticas[consultorId].valor_total += comissao.valor || 0;

        if (comissao.status_pagamento === 'PAGA') {
          estatisticas[consultorId].comissoes_pagas++;
          estatisticas[consultorId].valor_pago += comissao.valor || 0;
        } else if (comissao.status_pagamento === 'PENDENTE') {
          estatisticas[consultorId].comissoes_pendentes++;
          estatisticas[consultorId].valor_pendente += comissao.valor || 0;
        }
      });

      return Object.values(estatisticas);
    } catch (error) {
      console.error('Erro ao obter estatísticas por consultor:', error);
      throw error;
    }
  }

  /**
   * Obtém relatório de performance financeira
   */
  async getRelatorioPerformance(tenantId: string, periodo: DateRange): Promise<any> {
    try {
      const [
        resumoFinanceiro,
        fluxoCaixa,
        estatisticasConsultores
      ] = await Promise.all([
        this.getResumoFinanceiro(tenantId, periodo),
        this.getFluxoCaixa(tenantId, 6),
        this.getEstatisticasPorConsultor(tenantId, periodo)
      ]);

      return {
        resumo: resumoFinanceiro,
        fluxo_caixa: fluxoCaixa,
        estatisticas_consultores: estatisticasConsultores,
        periodo,
        gerado_em: new Date().toISOString()
      };
    } catch (error) {
      console.error('Erro ao gerar relatório de performance:', error);
      throw error;
    }
  }
}

// Instância singleton do serviço
export const financeiroSupabaseService = new FinanceiroSupabaseService();

